/* eslint-disable @nrwl/nx/enforce-module-boundaries */
import { Injectable } from '@angular/core';
import {
  EChartType,
  EChartsService,
  IEChartFactory,
  EWidget,
  IWidgets,
  EChartSorting,
} from '@midas/shared/common';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { OverviewStatisticActions, OverviewStatisticsSelectors } from '@midas/overview/data-access';

@Injectable({ providedIn: 'root' })
export class OverviewStatisticsService {
  constructor(private store: Store, private echartsService: EChartsService) {}

  getOverviewChartData = (
    selector: any,
    chartFactory: IEChartFactory,
  ): Observable<echarts.EChartsOption> | undefined => {
    return new Observable<echarts.EChartsOption>((observable) => {
      this.store.select(selector).subscribe((selectorData: any) => {
        if (!selectorData) return;

        let chartDataObservable: echarts.EChartsOption | undefined = undefined;

        switch (chartFactory.type) {
          case EChartType.gauge: {
            chartDataObservable = this.echartsService.eChartsFactory({ ...chartFactory, data: selectorData });
            break;
          }
          case EChartType.pie:
          case EChartType.bar: {
            const source = selectorData.map((singleSelectorData: any) =>
              chartFactory.datasetKeys?.map((singlekey: any) => singleSelectorData[singlekey]),
            );
            chartDataObservable = this.echartsService.eChartsFactory({ ...chartFactory, data: source });
            break;
          }
          case EChartType.pieDoughnut: {
            const source = selectorData.map((singleSelectorData: any) => {
              const tempValues = {
                value: 0,
                name: '',
              };
              if (chartFactory.pieNameKey && singleSelectorData[chartFactory.pieNameKey]) {
                tempValues.name = singleSelectorData[chartFactory.pieNameKey];
              }

              if (chartFactory.pieValuekey && singleSelectorData[chartFactory.pieValuekey]) {
                tempValues.value = singleSelectorData[chartFactory.pieValuekey];
              }

              return tempValues;
            });
            chartDataObservable = this.echartsService.eChartsFactory({ ...chartFactory, data: source });
            break;
          }
          default:
            return;
        }
        observable.next(chartDataObservable);

        // FOR TESTING PORPUSES
        // observable.complete();
      });
    });
  };

  getOverviewWidgets = (startDate: string, endDate: string): IWidgets[] => {
    return [
      {
        type: EWidget.numberOfWaste,
        title: 'Number of Waste',
        data: this.getOverviewChartData(OverviewStatisticsSelectors.getOverviewStatisticsNumberOfWaste, {
          type: EChartType.gauge,
        }),
        loaded: this.store.select(OverviewStatisticsSelectors.areNumberOfWasteLoaded),
        action: OverviewStatisticActions.queryNumberOfWaste({ startDate, endDate }),
      },
      {
        type: EWidget.numberOfWitness,
        title: 'Number of Anytime Witness Waste',
        data: this.getOverviewChartData(OverviewStatisticsSelectors.getOverviewStatisticsNumberOfWitness, {
          type: EChartType.gauge,
        }),
        loaded: this.store.select(OverviewStatisticsSelectors.areNumberOfWitnessLoaded),
        action: OverviewStatisticActions.queryNumberOfWitness({ startDate, endDate }),
      },
      {
        type: EWidget.numberOfStandardWaste,
        title: 'Non-Controlled Waste',
        data: this.getOverviewChartData(
          OverviewStatisticsSelectors.getOverviewStatisticsNumberOfStandardWaste,
          { type: EChartType.gauge },
        ),
        loaded: this.store.select(OverviewStatisticsSelectors.areNumberOfStandardWasteLoaded),
        action: OverviewStatisticActions.queryNumberOfStandardWaste({ startDate, endDate }),
      },
      {
        type: EWidget.rxInterceptWasteByDateRange,
        title: 'Rx Intercept Waste by Date Range',
        data: this.getOverviewChartData(
          OverviewStatisticsSelectors.getOverviewStatisticsRxInterceptWasteByDateRange,
          { type: EChartType.gauge },
        ),
        loaded: this.store.select(OverviewStatisticsSelectors.areRxInterceptWasteByDateRangeLoaded),
        action: OverviewStatisticActions.queryRxInterceptWasteByDateRange({ startDate, endDate }),
      },
      {
        type: EWidget.wasteCountByPerson,
        title: 'Top 10 Waste Counts by Person',
        data: this.getOverviewChartData(OverviewStatisticsSelectors.getOverviewStatisticsWasteByPerson, {
          type: EChartType.bar,
          datasetKeys: ['user', 'amountOfWaste'],
          encode: { x: 'user', y: 'amountOfWaste' },
          sort: EChartSorting.desc,
          sortValue: 'amountOfWaste',
        }),
        loaded: this.store.select(OverviewStatisticsSelectors.areWasteByPersonLoaded),
        action: OverviewStatisticActions.queryWasteByPerson({ startDate, endDate }),
      },

      {
        type: EWidget.wasteCountByHospital,
        title: 'Waste Counts by Hospital',
        data: this.getOverviewChartData(
          OverviewStatisticsSelectors.getOverviewStatisticsNumberOfWasteByHospital,
          { type: EChartType.pie, datasetKeys: ['locationName', 'amountOfWaste'] },
        ),
        loaded: this.store.select(OverviewStatisticsSelectors.areNumberOfWasteByHospitalLoaded),
        action: OverviewStatisticActions.queryNumberOfWasteByHospital({ startDate, endDate }),
      },
      {
        type: EWidget.numberOfWitnessByHospital,
        title: 'Witness Waste Counts by Hospital',
        data: this.getOverviewChartData(
          OverviewStatisticsSelectors.getOverviewStatisticsNumberOfWitnessByHospital,
          { type: EChartType.pie, datasetKeys: ['locationName', 'amountOfWaste'] },
        ),
        loaded: this.store.select(OverviewStatisticsSelectors.areNumberOfWitnessByHospitalLoaded),
        action: OverviewStatisticActions.queryNumberOfWitnessByHospital({ startDate, endDate }),
      },

      {
        type: EWidget.wasteCountByDrug,
        title: 'Top 10 Waste Counts by Drug',
        data: this.getOverviewChartData(OverviewStatisticsSelectors.getOverviewStatisticsWasteByDrug, {
          type: EChartType.bar,
          datasetKeys: ['drug', 'amountOfWaste'],
          encode: { x: 'drug', y: 'amountOfWaste' },
          sort: EChartSorting.desc,
          sortValue: 'amountOfWaste',
        }),
        loaded: this.store.select(OverviewStatisticsSelectors.areWasteByDrugLoaded),
        action: OverviewStatisticActions.queryWasteByDrug({ startDate, endDate }),
      },

      {
        type: EWidget.rxInterceptWaste,
        title: 'Rx Intercept Waste',
        data: this.getOverviewChartData(OverviewStatisticsSelectors.getOverviewStatisticsRxInterceptWaste, {
          type: EChartType.pie,
          datasetKeys: ['locationName', 'amountOfWaste'],
        }),
        loaded: this.store.select(OverviewStatisticsSelectors.areRxInterceptWaste),
        action: OverviewStatisticActions.queryRxInterceptWaste({ startDate, endDate }),
      },

      {
        type: EWidget.standardWasteLocation,
        title: 'Number of Standard Waste Grouped by Hospital',
        data: this.getOverviewChartData(
          OverviewStatisticsSelectors.getOverviewStatisticsStandardWastelocation,
          { type: EChartType.pieDoughnut, pieValuekey: 'amountOfWaste', pieNameKey: 'locationName' },
        ),
        loaded: this.store.select(OverviewStatisticsSelectors.areStandardWasteLocationLoaded),
        action: OverviewStatisticActions.queryStandardWasteLocation({ startDate, endDate }),
      },
    ];
  };
}
